import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Menu from '../Menu/Menu';
import '../../styles/core.css';
import api from '../../utils/axios.secure';

const ATTENTION_CONTENTS = new Set(['AreaInfo', 'AreaByTime']);

const columns = [
  ['idx', '번호'],
  ['user_id', '아동 번호'],
  ['content_name', '분석 유형'],
  ['gameMode', '모드'],
  ['focusRatio', '집중률'],
  ['onScreenCount', '화면 안'],
  ['offScreenCount', '화면 밖'],
  ['onScreenTime', '응시 시간'],
  ['offScreenTime', '이탈 시간'],
  ['saveDate', '저장 일시'],
];

function unwrapRows(res) {
  const payload = res?.data || res;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload)) return payload;
  return [];
}

function valueOf(row, key, fallback = '-') {
  const value = row?.[key];
  return value === null || value === undefined || value === '' ? fallback : value;
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function pickNumber(source, keys) {
  for (const key of keys) {
    const n = toNumber(source?.[key]);
    if (n !== null) return n;
  }
  return null;
}

function getPayload(row) {
  return row?.payload && typeof row.payload === 'object' ? row.payload : {};
}

function getAttentionType(row) {
  const payload = getPayload(row);
  const explicitType =
    row?.content_name ??
    row?.contentName ??
    row?.AP_contentName ??
    row?.content ??
    payload?.content_name ??
    payload?.contentName ??
    payload?.AP_contentName ??
    payload?.content ??
    payload?.type ??
    payload?.name ??
    '';

  const normalized = String(explicitType || '').trim();
  if (ATTENTION_CONTENTS.has(normalized)) return normalized;

  const keys = Object.keys(payload);
  const hasAreaByTime =
    Array.isArray(payload?.areaByTime) ||
    Array.isArray(payload?.AreaByTime) ||
    Array.isArray(payload?.timeline) ||
    Array.isArray(payload?.points) ||
    keys.some((key) => key.toLowerCase().includes('bytime'));

  if (hasAreaByTime) return 'AreaByTime';

  const hasAreaInfo =
    keys.some((key) => key.toLowerCase().includes('area')) ||
    ['AP_onScreenCount', 'AP_offScreenCount', 'AP_onScreenTime', 'AP_offScreenTime']
      .some((key) => payload?.[key] !== undefined || row?.[key] !== undefined);

  if (hasAreaInfo) return 'AreaInfo';

  return '';
}

function isAttentionRow(row) {
  return ATTENTION_CONTENTS.has(getAttentionType(row));
}

function formatNumber(value, digits = 1) {
  const n = toNumber(value);
  if (n === null) return '-';
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(digits);
}

function summarizeAttention(row) {
  const payload = getPayload(row);
  const source = { ...payload, ...row };
  const contentName = getAttentionType(row);

  const onScreenCount = pickNumber(source, [
    'AP_onScreenCount',
    'onScreenCount',
    'areaOnScreenCount',
    'focusCount',
    'correctCount',
  ]);
  const offScreenCount = pickNumber(source, [
    'AP_offScreenCount',
    'offScreenCount',
    'areaOffScreenCount',
    'outCount',
    'missCount',
    'wrongCount',
  ]);
  const onScreenTime = pickNumber(source, [
    'AP_onScreenTime',
    'onScreenTime',
    'areaOnScreenTime',
    'focusTime',
    'correctTime',
  ]);
  const offScreenTime = pickNumber(source, [
    'AP_offScreenTime',
    'offScreenTime',
    'areaOffScreenTime',
    'outTime',
  ]);

  let focusRatio = pickNumber(source, ['focusRatio', 'attentionRatio', 'accuracy']);
  if (focusRatio === null && onScreenTime !== null && offScreenTime !== null) {
    const total = onScreenTime + offScreenTime;
    focusRatio = total > 0 ? (onScreenTime / total) * 100 : null;
  } else if (focusRatio === null && onScreenCount !== null && offScreenCount !== null) {
    const total = onScreenCount + offScreenCount;
    focusRatio = total > 0 ? (onScreenCount / total) * 100 : null;
  }

  return {
    ...row,
    content_name: contentName || row?.content_name || row?.contentName || row?.AP_contentName || '-',
    onScreenCount,
    offScreenCount,
    onScreenTime,
    offScreenTime,
    focusRatio,
  };
}

function getPayloadPreview(row) {
  if (!row?.payload || typeof row.payload !== 'object') return 'payload 없음';
  return JSON.stringify(row.payload, null, 2);
}

function Attentionspan() {
  const [rows, setRows] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  const loadAttentionData = useCallback(async () => {
    setLoading(true);
    setMessage('');

    try {
      const { data: res } = await api.post('/manager/content/contentResult', {});
      const nextRows = unwrapRows(res)
        .filter(isAttentionRow)
        .map(summarizeAttention);

      setRows(nextRows);
      setMessage(nextRows.length ? '' : 'AreaInfo 또는 AreaByTime 결과가 없습니다.');
    } catch (error) {
      console.error('attention content result error:', error);
      setRows([]);
      setMessage(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          '주의 집중 분석 데이터를 불러오는 중 오류가 발생했습니다.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAttentionData();
  }, [loadAttentionData]);

  const filteredRows = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      columns.some(([key]) => String(row?.[key] ?? '').toLowerCase().includes(query)) ||
      getPayloadPreview(row).toLowerCase().includes(query)
    );
  }, [filterText, rows]);

  const stats = useMemo(() => {
    const areaInfo = rows.filter((row) => row.content_name === 'AreaInfo').length;
    const areaByTime = rows.filter((row) => row.content_name === 'AreaByTime').length;
    const children = new Set(rows.map((row) => row.user_id).filter(Boolean)).size;
    const ratios = rows.map((row) => toNumber(row.focusRatio)).filter((n) => n !== null);
    const avgRatio = ratios.length
      ? ratios.reduce((sum, n) => sum + n, 0) / ratios.length
      : null;

    return [
      { label: '전체 기록', value: rows.length.toLocaleString(), hint: 'AreaInfo + AreaByTime', tone: 'blue' },
      { label: '대상 아동', value: children.toLocaleString(), hint: '고유 아동 수', tone: 'teal' },
      { label: 'AreaInfo', value: areaInfo.toLocaleString(), hint: '영역 요약', tone: 'amber' },
      { label: '평균 집중률', value: avgRatio === null ? '-' : `${avgRatio.toFixed(1)}%`, hint: '계산 가능 항목', tone: 'green' },
      { label: 'AreaByTime', value: areaByTime.toLocaleString(), hint: '시간별 영역', tone: 'blue' },
    ];
  }, [rows]);

  const exportCsv = () => {
    const header = columns.map(([, label]) => label).join(',');
    const lines = filteredRows.map((row) =>
      columns
        .map(([key]) => `"${String(row?.[key] ?? '').replace(/"/g, '""')}"`)
        .join(',')
    );
    const blob = new Blob(['\uFEFF' + [header, ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'attention-analysis.csv');
    link.click();
    URL.revokeObjectURL(url);
  };

  const openDetail = (row) => {
    const index = row?.idx;
    if (index !== null && index !== undefined && index !== '') {
      navigate(`/attention/${index}`);
    }
  };

  return (
    <Menu>
      <section className="screen attention-screen">
        <div className="page-tabs" aria-label="주의 집중 분석 보기 옵션">
          <button type="button" className="tab active">
            영역 분석
          </button>
          <button type="button" className="tab" disabled>
            시간대 비교
          </button>
          <button type="button" className="tab" disabled>
            리포트
          </button>
        </div>

        <div className="screen-head">
          <div>
            <p className="eyebrow">Attention</p>
            <h1 className="screen-title">주의 집중 분석</h1>
            <p className="screen-copy">AreaInfo와 AreaByTime 결과만 모아 시선 집중 흐름을 확인합니다.</p>
          </div>
          <div className="screen-actions">
            <button type="button" className="btn btn-outline" onClick={loadAttentionData} disabled={loading}>
              새로고침
            </button>
            <button type="button" className="btn btn-primary" onClick={exportCsv} disabled={!filteredRows.length}>
              내보내기
            </button>
          </div>
        </div>

        <div className="kpi-grid attention-kpi-grid">
          {stats.map((item) => (
            <div className="kpi-card" key={item.label}>
              <span className={`kpi-accent ${item.tone}`} />
              <p className="kpi-label">{item.label}</p>
              <strong className="kpi-value">{item.value}</strong>
              <div className="kpi-meta">
                <span className="kpi-period">{item.hint}</span>
              </div>
            </div>
          ))}
        </div>

        {(message || loading) && (
          <div className={loading ? 'notice info' : 'notice'}>
            {loading ? '주의 집중 데이터를 불러오는 중입니다.' : message}
          </div>
        )}

        <div className="attention-layout">
          <div className="card content-table-card">
            <div className="card-header">
              <div>
                <h2 className="card-title">분석 기록</h2>
                <p className="card-subtitle">
                  총 {rows.length.toLocaleString()}건 중 {filteredRows.length.toLocaleString()}건 표시
                </p>
              </div>
            </div>

            <div className="search-row">
              <input
                className="search-input"
                type="text"
                placeholder="아동 번호, 분석 유형, payload로 검색"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </div>

            <div className="table-wrap">
              <table className="data-table attention-table">
                <thead>
                  <tr>
                    {columns.map(([, label]) => (
                      <th key={label}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, index) => (
                    <tr
                      className="clickable"
                      key={`${valueOf(row, 'idx', index)}-${index}`}
                      onClick={() => openDetail(row)}
                    >
                      <td>{valueOf(row, 'idx')}</td>
                      <td>{valueOf(row, 'user_id')}</td>
                      <td>
                        <span className="badge badge-blue">{valueOf(row, 'content_name')}</span>
                      </td>
                      <td>{valueOf(row, 'gameMode')}</td>
                      <td>{row.focusRatio === null ? '-' : `${formatNumber(row.focusRatio)}%`}</td>
                      <td>{formatNumber(row.onScreenCount, 0)}</td>
                      <td>{formatNumber(row.offScreenCount, 0)}</td>
                      <td>{formatNumber(row.onScreenTime)}</td>
                      <td>{formatNumber(row.offScreenTime)}</td>
                      <td>{valueOf(row, 'saveDate')}</td>
                    </tr>
                  ))}
                  {!filteredRows.length && (
                    <tr>
                      <td className="empty-cell" colSpan={columns.length}>
                        표시할 데이터가 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="table-footer">
              <span>행을 클릭하면 payload 상세 화면으로 이동합니다.</span>
              <span>{filteredRows.length.toLocaleString()} rows</span>
            </div>
          </div>

          <div className="card attention-preview-card">
            <div className="card-header">
              <div>
                <h2 className="card-title">최근 payload</h2>
                <p className="card-subtitle">가장 최근 AreaInfo/AreaByTime 복호화 결과입니다.</p>
              </div>
            </div>
            <pre className="payload-preview">
              {filteredRows[0] ? getPayloadPreview(filteredRows[0]) : 'payload 없음'}
            </pre>
          </div>
        </div>
      </section>
    </Menu>
  );
}

export default Attentionspan;
